import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicEvents } from '@/services/events.api';
import type { FrontendEvent } from '@/services/transform';

interface GalleryItem {
  url: string;
  eventId: string | number;
  eventTitle: string;
}

const AUTOPLAY_MS = 4000;

export default function EventsGallery() {
  const { i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    async function load() {
      try {
        const events = await getPublicEvents();
        const past = events.filter((e: FrontendEvent) => e.gallery && e.gallery.length > 0);
        const flat: GalleryItem[] = [];
        for (const ev of past) {
          const title = isZh ? ev.title : (ev.titleEn || ev.title);
          for (const url of (ev.gallery || [])) {
            flat.push({ url, eventId: ev.id, eventTitle: title });
          }
        }
        setItems(flat);
      } catch (e) {
        console.error('Failed to load gallery:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [isZh]);

  // Autoplay
  useEffect(() => {
    if (items.length <= 1 || hover || lightboxOpen) return;
    const t = setInterval(() => setCurrent((p) => (p + 1) % items.length), AUTOPLAY_MS);
    return () => clearInterval(t);
  }, [items.length, hover, lightboxOpen]);

  const goPrev = () => setCurrent((p) => (p - 1 + items.length) % items.length);
  const goNext = () => setCurrent((p) => (p + 1) % items.length);

  if (!loading && items.length === 0) return null;

  return (
    <section ref={ref} className="py-20 md:py-28 bg-[#F7F9FC]">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className={`text-center mb-12 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <p className="text-[#E8A838] text-sm font-semibold tracking-widest uppercase mb-2">
            {isZh ? '活动相册' : 'Event Gallery'}
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#0A2540]">
            {isZh ? '精彩瞬间' : 'Memorable Moments'}
          </h2>
        </div>

        {items.length > 0 && (
          <div
            className={`relative rounded-2xl overflow-hidden bg-black shadow-lg transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
          >
            <div
              className="relative aspect-[16/9] cursor-pointer group"
              onClick={() => setLightboxOpen(true)}
            >
              {items.map((item, idx) => (
                <img
                  key={`${item.eventId}-${idx}`}
                  src={item.url}
                  alt={item.eventTitle}
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === current ? 'opacity-100' : 'opacity-0'}`}
                />
              ))}

              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent p-6 pointer-events-none">
                <p className="text-white text-sm md:text-base font-medium truncate">{items[current].eventTitle}</p>
                <p className="text-white/60 text-xs mt-1">{current + 1} / {items.length}</p>
              </div>
            </div>

            {items.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); goPrev(); }}
                  className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center text-[#0A2540] transition-all"
                  aria-label="Previous"
                >
                  <i className="ri-arrow-left-s-line text-2xl" />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); goNext(); }}
                  className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-white/90 hover:bg-white shadow-lg flex items-center justify-center text-[#0A2540] transition-all"
                  aria-label="Next"
                >
                  <i className="ri-arrow-right-s-line text-2xl" />
                </button>
              </>
            )}
          </div>
        )}

        {/* Thumbnail strip */}
        {items.length > 1 && (
          <div className="mt-4 flex gap-2 overflow-x-auto pb-2 scrollbar-thin">
            {items.slice(0, 20).map((item, idx) => (
              <button
                key={`thumb-${idx}`}
                onClick={() => setCurrent(idx)}
                className={`relative shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden transition-all ${idx === current ? 'ring-2 ring-[#2C7DA0] ring-offset-2' : 'opacity-60 hover:opacity-100'}`}
              >
                <img src={item.url} alt="" loading="lazy" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}

        <div className="text-center mt-10">
          <Link
            to="/events"
            className="inline-flex items-center gap-2 px-6 py-3 bg-[#2C7DA0] hover:bg-[#1a5a7a] text-white rounded-lg text-sm font-medium transition-colors"
          >
            {isZh ? '查看全部活动' : 'View All Events'}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            className="absolute top-4 right-4 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
          >
            <i className="ri-close-line text-2xl" />
          </button>
          <div className="absolute top-4 left-4 px-4 py-2 rounded-full bg-white/10 text-white text-sm">
            {current + 1} / {items.length}
          </div>
          {items.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); goPrev(); }}
              className="absolute left-4 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            >
              <i className="ri-arrow-left-s-line text-3xl" />
            </button>
          )}
          <img
            src={items[current].url}
            alt={items[current].eventTitle}
            className="max-w-[90vw] max-h-[85vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {items.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); goNext(); }}
              className="absolute right-4 w-14 h-14 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"
            >
              <i className="ri-arrow-right-s-line text-3xl" />
            </button>
          )}
        </div>
      )}
    </section>
  );
}
